import { useEffect, useState } from 'react'

/**
 * Backoff for re-reading an entity that answered 404.
 *
 * Sized for a storage consistency lag, not for an outage: ~3.75s total, front-loaded so the
 * common case clears in a few hundred milliseconds. The cost of the window is paid only by a
 * genuine 404 — a link to something deleted takes this long to say so — which is the trade the
 * issue asks for, and the reason the window is bounded rather than open-ended.
 */
export const retryDelaysMs = [250, 500, 1000, 2000]

const isNotFound = (error?: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return false
  }
  return (error as { status: unknown }).status === 404
}

export interface SettlingEntityRead {
  /** The read answered 404 but the window is still open — show progress, not the error. */
  isSettling: boolean
  /** Reopen the window and read again. Backs the "Try again" affordance. */
  retry(): void
}

/**
 * Re-read an entity that answered 404, for a bounded window, before believing it.
 *
 * GH #1842: a read landing before the entity is consistent returns 404, and the entity page
 * rendered a terminal "Page not found". Nothing recovered it — RTK had already resolved the query
 * with an error, so no amount of waiting re-fetched; only a manual reload did, and the page never
 * said so. Sharing a link to something you just created could tell your bandmate it does not exist.
 *
 * Only 404 is retried. Every other status is an answer rather than a lag, and reporting those
 * immediately is correct.
 *
 * The repo already knew this shape: `openPollUntilReady` and `gotoUntilEditorReady` in the
 * Playwright suite both hand-roll exactly this loop. Two test helpers worked around it; the
 * product did not.
 */
export const useSettlingEntityRead = ({
  entityId,
  error,
  refetch,
}: {
  entityId?: string | null
  error?: unknown
  refetch(): void
}): SettlingEntityRead => {
  const [attempt, setAttempt] = useState(0)

  /** A different entity is a different read, and gets its own window. */
  useEffect(() => {
    setAttempt(0)
  }, [entityId])

  const settling = isNotFound(error) && attempt < retryDelaysMs.length

  useEffect(() => {
    if (!settling) {
      return
    }
    const timer = setTimeout(() => {
      setAttempt(current => current + 1)
      refetch()
    }, retryDelaysMs[attempt])
    return () => {
      clearTimeout(timer)
    }
  }, [settling, attempt, refetch])

  const retry = () => {
    setAttempt(0)
    refetch()
  }

  return { isSettling: settling, retry }
}
